import React, { useEffect, useState } from "react";
import "./BannerData.css";
import { Button } from "antd";
import { BannerSkeleton } from "../../BannerSkeleton";
import {
  addHttpToLink,
  isNavigable,
  lightenColor,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import {
  getBannerImageSources,
  getBannerImageClassName,
} from "../../../../Utils/imageCrop/bannerImageDisplay";
import { isHexDark } from "../../../../Utils/HelperFunction/HelperFunction";
import { darkenColor } from "../../../../Screens/DashboardView/StayUpToDate/LandingPageComponents/LinksComponent/Components/hoverUtils";
import {
  hasWebsiteLibBannerQuery,
  bannerOptionsForClassicLib,
} from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteLibBannerOptions";
import {
  bannerImageFrameStyle,
  normalizeBannerImageFrame,
} from "../../../../Screens/DashboardView/MasterTemplateEditor/templateBannerUtils";
import { btnClass } from "../../../../Screens/DashboardView/MasterTemplateEditor/sections/sectionUtils";
import { TemplateMarqueeTrack } from "../../../../Screens/DashboardView/MasterTemplateEditor/TemplateMarqueeTrack";

/** Session-scoped: banner content-arrive runs once per component id. */
const bannerContentArrivedIds = new Set<string>();

function websiteShellStyle(
  bgTone: string,
  opts?: { fullBleed?: boolean },
): React.CSSProperties {
  const tone = String(bgTone || "bg");
  const gutter = "var(--tpl-gutter, 20px)";
  return {
    ["--tpl-shell-bg" as string]:
      tone === "surface"
        ? "var(--tpl-surface, #f4f4f5)"
        : tone === "accent"
          ? "var(--tpl-accent, #111)"
          : "var(--tpl-bg, #fff)",
    ["--tpl-shell-fg" as string]:
      tone === "accent"
        ? "var(--tpl-accent-ink, #fff)"
        : "var(--tpl-ink, #111)",
    ["--tpl-shell-muted" as string]:
      tone === "accent"
        ? "var(--tpl-accent-ink, #fff)"
        : "var(--tpl-muted, #7a776e)",
    ["--tpl-shell-pad" as string]: opts?.fullBleed ? "0" : `0 ${gutter} 0`,
  } as React.CSSProperties;
}

const BannerData = (props: any) => {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 400);
  }, []);
  const { title, component, handleRedirect, from, designTokens } = props;
  const websiteLayout = hasWebsiteLibBannerQuery(component?.query);
  const bannerOpts = bannerOptionsForClassicLib(component?.query);
  const showCta =
    bannerOpts.showCta !== false &&
    Boolean(component?.cta_placeholder && component?.cta_placeholder !== "");
  const subtitle =
    bannerOpts.showSubtitle === false
      ? ""
      : String(component?._liveSubtitle || bannerOpts.subtitle || "").trim();
  const eyebrow =
    bannerOpts.showEyebrow === true
      ? String(bannerOpts.eyebrow || "").trim()
      : "";
  const templateName = designTokens.templateName;
  const effectiveTemplateName = (templateName || "").toLowerCase();
  const isDefaultTheme = effectiveTemplateName === "default";
  const buttonBg = isDefaultTheme
    ? "#000"
    : designTokens?.forthColor || "#000";
  const buttonOverrideClass = isDefaultTheme ? "banner-btn-override" : "";
  const bannerImageUrl =
    String(component?.details ?? "").split(",")[0]?.trim() ||
    String(component?.original_banner_image ?? "").trim() ||
    "/assets/image-bg.jpeg";
  const bannerImage = getBannerImageSources(bannerImageUrl);
  const ctaLabel = String(component?.cta_placeholder || "").trim() || "Shop Now";
  const bannerTitle = String(component?.name || title || "").trim();
  const linkRaw = String(component?.details ?? "").split(",")[1]?.trim() || "";
  const href = linkRaw
    ? linkRaw.startsWith("http")
      ? linkRaw
      : `http://${linkRaw}`
    : "#";

  const openBannerLink = (e?: React.MouseEvent, via: string = "") => {
    e?.preventDefault();
    handleRedirect?.(
      component.id,
      component?.details?.split(",")[0],
      via,
      title,
      title,
      "",
      from,
    );
    if (linkRaw) {
      window.open(addHttpToLink(linkRaw), "_blank");
    }
  };

  const frameStyle = bannerImageFrameStyle(
    normalizeBannerImageFrame({
      imageZoom: bannerOpts.imageZoom,
      imageX: bannerOpts.imageX,
      imageY: bannerOpts.imageY,
    }),
  );

  const banImg = (cls: string) => {
    const node = (
      <img
        src={bannerImage.src}
        srcSet={bannerImage.srcSet}
        sizes={bannerImage.sizes}
        alt={bannerTitle || title}
        className={`mte-tpl-img-filter ${cls}`}
        style={frameStyle}
        decoding="async"
      />
    );
    return cls.includes("mte-tpl-ban-split-img") ? (
      <div className="mte-tpl-ban-split-frame">{node}</div>
    ) : (
      node
    );
  };

  if (loading) {
    return <BannerSkeleton designTokens={designTokens} isBannerData={true} />;
  }

  // First paint of real banner data only — skip if this banner already arrived
  // (control/layout remounts must not re-animate).
  const bannerArriveKey = String(component?.id || component?.unique_id || "");
  const wrapArrive = (node: React.ReactNode) => {
    if (!bannerArriveKey || bannerContentArrivedIds.has(bannerArriveKey)) {
      return <>{node}</>;
    }
    bannerContentArrivedIds.add(bannerArriveKey);
    return <div className="sb-animate-content-arrive">{node}</div>;
  };

  if (websiteLayout) {
    const style = bannerOpts.style;
    const bgTone =
      bannerOpts.bgTone ||
      (style === "colored" || style === "ribbon" ? "accent" : "bg");
    // Match GhostBanner: normalized overlay-left (legacy fullbleed) is edge-to-edge.
    const fullBleed =
      style === "overlay-left" ||
      style === "fullbleed" ||
      style === "overlay-center" ||
      style === "ribbon";

    const sectionTitle = String(component?.name || title || "").trim();
    // Classic LiB carousels / legacy banners put the name in `.mainDiv`. Website
    // layout used to skip it (title only on the media via cqi), so public LiB
    // lost the heading when outside `.mte-tpl-page` containment.
    const showLegacyLibTitle = from === "landing-page" && Boolean(sectionTitle);

    const body =
      style === "colored" ? (
        <div className="mte-tpl-ban-colored">
          {bannerTitle ? (
            <h2 className="mte-tpl-ban-colored-h2">{bannerTitle}</h2>
          ) : null}
          {subtitle ? (
            <p className="mte-tpl-ban-colored-body">{subtitle}</p>
          ) : null}
          {showCta ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className={`${btnClass("invertOnAccent")} text-decoration-none`}
              onClick={(e) => openBannerLink(e)}
            >
              {ctaLabel}
            </a>
          ) : null}
        </div>
      ) : style === "split" ? (
        <div className="mte-tpl-ban-split">
          <div
            className="mte-tpl-ban-split-frame"
            role={linkRaw ? "link" : undefined}
            tabIndex={linkRaw ? 0 : undefined}
            onClick={(e) => openBannerLink(e, "banner")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                openBannerLink(undefined, "banner");
            }}
            style={{ cursor: linkRaw ? "pointer" : undefined }}
          >
            <img
              src={bannerImage.src}
              srcSet={bannerImage.srcSet}
              sizes={bannerImage.sizes}
              alt={bannerTitle || title}
              className="mte-tpl-img-filter mte-tpl-ban-split-img"
              style={frameStyle}
              decoding="async"
            />
          </div>
          <div className="mte-tpl-ban-split-copy">
            {eyebrow ? (
              <div className="mte-tpl-ban-eyebrow">{eyebrow}</div>
            ) : null}
            {bannerTitle ? (
              <h2 className="mte-tpl-ban-split-h2">{bannerTitle}</h2>
            ) : null}
            {subtitle ? (
              <p className="mte-tpl-ban-split-sub">{subtitle}</p>
            ) : null}
            {showCta ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={`${btnClass("primary")} mte-tpl-btn--full-width text-decoration-none`}
                onClick={(e) => openBannerLink(e)}
              >
                {ctaLabel}
              </a>
            ) : null}
          </div>
        </div>
      ) : style === "overlay-center" ? (
        <div
          className="mte-tpl-ban-ov"
          role={linkRaw ? "link" : undefined}
          tabIndex={linkRaw ? 0 : undefined}
          onClick={(e) => openBannerLink(e, "banner")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openBannerLink(undefined, "banner");
          }}
          style={{ cursor: linkRaw ? "pointer" : undefined }}
        >
          {banImg("mte-tpl-ban-ov-img")}
          <div className="mte-tpl-ban-ov-scrim" />
          <div className="mte-tpl-ban-ov-inner">
            {eyebrow ? (
              <div className="mte-tpl-ban-ov-eyebrow">{eyebrow}</div>
            ) : null}
            {bannerTitle ? (
              <h2 className="mte-tpl-ban-ov-h2">{bannerTitle}</h2>
            ) : null}
            {subtitle ? (
              <p className="mte-tpl-ban-ov-sub">{subtitle}</p>
            ) : null}
            {showCta ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={`${btnClass("onImage")} mte-tpl-btn--mt-8 text-decoration-none`}
                onClick={(e) => {
                  e.stopPropagation();
                  openBannerLink(e);
                }}
              >
                {ctaLabel} →
              </a>
            ) : null}
          </div>
        </div>
      ) : style === "ribbon" ? (
        <div className="mte-tpl-ban-ribbon">
          {bannerTitle || subtitle ? (
            <TemplateMarqueeTrack
              mode="ticker"
              key={`${bannerTitle}|${subtitle}`}
              outerClassName="mte-tpl-ban-ribbon-marquee"
              trackClassName="mte-tpl-ban-ribbon-track mte-tpl-ban-ribbon-track--js"
            >
              <span className="mte-tpl-ban-ribbon-txt">
                {bannerTitle}
                {bannerTitle && subtitle ? ` · ${subtitle}` : subtitle}
              </span>
            </TemplateMarqueeTrack>
          ) : null}
          {showCta ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className={`${btnClass("invertOnAccent")} mte-tpl-ban-ribbon-cta text-decoration-none`}
              onClick={(e) => openBannerLink(e)}
            >
              {ctaLabel} →
            </a>
          ) : null}
        </div>
      ) : (
        <div
          className="mte-tpl-ban-fb"
          role={linkRaw ? "link" : undefined}
          tabIndex={linkRaw ? 0 : undefined}
          onClick={(e) => openBannerLink(e, "banner")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openBannerLink(undefined, "banner");
          }}
          style={{ cursor: linkRaw ? "pointer" : undefined }}
        >
          {banImg("mte-tpl-ban-fb-img")}
          <div className="mte-tpl-ban-fb-grad" />
          <div className="mte-tpl-ban-fb-bar">
            <div>
              {eyebrow ? (
                <div className="mte-tpl-ban-fb-eyebrow">{eyebrow}</div>
              ) : null}
              {bannerTitle ? (
                <h2 className="mte-tpl-ban-fb-h2">{bannerTitle}</h2>
              ) : null}
              {subtitle ? (
                <p className="mte-tpl-ban-fb-sub">{subtitle}</p>
              ) : null}
            </div>
            {showCta ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={`${btnClass("onImage")} mte-tpl-btn--nowrap text-decoration-none`}
                onClick={(e) => {
                  e.stopPropagation();
                  openBannerLink(e);
                }}
              >
                {ctaLabel}
              </a>
            ) : null}
          </div>
        </div>
      );

    return wrapArrive(
      <>
        {showLegacyLibTitle ? (
          <div className="mainDiv">
            <p
              className="component-name"
              style={{
                cursor: isNavigable({
                  display_name: component.name,
                  type: component.type,
                })
                  ? "pointer"
                  : "default",
                fontFamily: designTokens?.primaryFontFamily,
              }}
            >
              {sectionTitle}
            </p>
          </div>
        ) : null}
        <div
          className="mte-tpl-shell mte-lib-banner-live"
          data-template-shell="true"
          data-template-shell-tone={bgTone}
          {...(fullBleed ? { "data-template-shell-fullbleed": "true" } : {})}
          data-banner-style={style}
          data-banner-bg={bgTone}
          style={websiteShellStyle(bgTone, { fullBleed })}
        >
          {body}
        </div>
      </>,
    );
  }

  return wrapArrive(
    <>
      {isDefaultTheme && (
        <style>{`
          .uptodate-wrapper .banner-btn-override,
          .uptodate-wrapper .banner-btn-override:hover,
          .uptodate-wrapper .banner-btn-override:focus,
          .uptodate-wrapper .banner-btn-override:active {
            color: #fff !important;
          }
          .uptodate-wrapper .banner-btn-override span { color: #fff !important; }
          .default-theme-button:hover {
            background: #302f2f !important;
            border-color: #302f2f !important;
          }
        `}</style>
      )}
      <>
        <div
          className="mainDiv"
          data-banner-style={bannerOpts.style}
          data-banner-bg={bannerOpts.bgTone}
        >
          {eyebrow ? (
            <p
              className="component-name"
              style={{
                opacity: 0.7,
                fontSize: "0.75em",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 4,
                fontFamily: designTokens?.primaryFontFamily,
              }}
            >
              {eyebrow}
            </p>
          ) : null}
          <p
            className="component-name"
            style={{
              cursor: isNavigable({
                display_name: component.name,
                type: component.type,
              })
                ? "pointer"
                : "default",
              fontFamily: designTokens?.primaryFontFamily,
            }}
          >
            {component.name}
          </p>
          {subtitle ? (
            <p
              className="component-name"
              style={{
                opacity: 0.75,
                fontSize: "0.9em",
                fontWeight: 400,
                marginTop: 4,
                fontFamily: designTokens?.secondaryFontFamily,
              }}
            >
              {subtitle}
            </p>
          ) : null}

          {showCta ? (
            <a
              href={href}
              target="_blank"
              onClick={(e) => openBannerLink(e)}
              className="text-decoration-none"
            >
              {" "}
              <Button
                className={`highlight-shop-button ${
                  isDefaultTheme
                    ? "default-theme-button"
                    : designTokens?.secondaryColor
                      ? "custom-button-with-hover"
                      : ""
                } ${buttonOverrideClass}`}
                style={{
                  ...(isDefaultTheme && {
                    backgroundColor: buttonBg,
                    color: isDefaultTheme
                      ? "#fff"
                      : designTokens?.tertiaryColor || "#fff",
                  }),
                  fontFamily: designTokens?.secondaryFontFamily,
                  ...(designTokens?.secondaryFontFamily
                    ? ({
                        ["--highlight-font" as any]:
                          designTokens?.secondaryFontFamily,
                      } as any)
                    : {}),
                  ...(buttonBg
                    ? ({
                        ["--hover-bg" as any]: isHexDark(buttonBg)
                          ? lightenColor(buttonBg, 20)
                          : darkenColor(buttonBg, 10),
                      } as any)
                    : {}),
                }}
              >
                {" "}
                {component?.cta_placeholder}
              </Button>
            </a>
          ) : null}
        </div>
        <div className="banner-media-wrap">
          <div
            className="outter-section-banner"
            onClick={(e) => openBannerLink(e, "banner")}
            style={{
              border: "none",
            }}
          >
            <a href={href} target="_blank">
              <img
                src={bannerImage.src}
                srcSet={bannerImage.srcSet}
                sizes={bannerImage.sizes}
                alt={title}
                className={getBannerImageClassName(component)}
                decoding="async"
              />
            </a>
          </div>
        </div>
      </>
    </>,
  );
};

export default BannerData;
